import { Dialog } from "@headlessui/react";
import { formatPhoneNumber } from "../../../../../globalFunctions/PhoneNumberFormatter";
import { formatDateTime } from "../../../../../globalFunctions/DateTimeDisplayCleaner";
import {
  EnvelopeIcon,
  PhoneIcon,
  UserIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
} from "@heroicons/react/24/solid";
import { useState, useEffect } from "react";

const typeLabels = {
  1: {
    label: "En personne",
    icon: <UserIcon className="h-4 w-4 mr-1" />,
    color: "bg-pink-200",
  },
  2: {
    label: "Par téléphone",
    icon: <PhoneIcon className="h-4 w-4 mr-1" />,
    color: "bg-blue-200",
  },
  3: {
    label: "Par courriel",
    icon: <EnvelopeIcon className="h-4 w-4 mr-1" />,
    color: "bg-yellow-200",
  },
};

const statusLabels = {
  1: {
    label: "En cours",
    color: "bg-yellow-500 text-white",
    icon: <ClockIcon className="h-4 w-4 mr-1" />,
  },
  2: {
    label: "Complété",
    color: "bg-green-500 text-white",
    icon: <CheckCircleIcon className="h-4 w-4 mr-1" />,
  },
  3: {
    label: "Annulé",
    color: "bg-red-500 text-white",
    icon: <XCircleIcon className="h-4 w-4 mr-1" />,
  },
};

const allowedTransitions = {
  1: [2, 3],
  2: [],
  3: [],
};

export default function FollowUpDetailsModal({
  isOpen,
  onClose,
  followUp,
  onChangeStatus,
  onRequestComplete,
  onRequestCancel,
}) {
  const [status, setStatus] = useState(followUp?.status);

  if (!followUp) return null;

  const renderContactInfo = () => {
    if (followUp.followUpType === 1 && followUp.address) {
      return (
        <p>
          <strong>Adresse:</strong>{" "}
          <a
            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
              followUp.address
            )}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 underline"
          >
            {followUp.address}
          </a>
        </p>
      );
    }

    if (followUp.followUpType === 2 && followUp.phoneNumber) {
      return (
        <p>
          <strong>Téléphone:</strong>{" "}
          <a
            href={`tel:${formatPhoneNumber(followUp.phoneNumber.toString())}`}
            className="text-blue-600 underline"
          >
            {formatPhoneNumber(followUp.phoneNumber.toString())}
          </a>
        </p>
      );
    }

    if (followUp.followUpType === 3 && followUp.email) {
      return (
        <p>
          <strong>Email:</strong>{" "}
          <a
            href={`mailto:${followUp.email}`}
            className="text-blue-600 underline"
          >
            {followUp.email}
          </a>
        </p>
      );
    }

    return null;
  };

  const isOverdue =
    followUp.status === 1 && new Date(followUp.dueDate) <= new Date();
  const allowedNextStatuses = allowedTransitions[followUp.status] || [];

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      className="relative z-100 text-primary w-full max-w-[50%]"
    >
      <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl">
          <Dialog.Title className="text-xl font-bold mb-4">
            Détails du suivi
          </Dialog.Title>

          <div className="space-y-2 text-sm text-primary">
            <p>
              <strong>Nom:</strong> {followUp.userName || "-"}
            </p>

            {renderContactInfo()}

            <p>
              <strong>Note:</strong> {followUp.note || "-"}
            </p>

            <p>
              <strong>Date prévue:</strong> {formatDateTime(followUp.dueDate)}
              {isOverdue && (
                <span className="ml-2 inline-block px-2 py-1 bg-red-500 text-white rounded-full text-xs">
                  En retard
                </span>
              )}
            </p>

            {/* Type */}
            <p>
              <strong>Type:</strong>{" "}
              <span
                className={`inline-flex items-center px-2 py-1 rounded-full border ${
                  typeLabels[followUp.followUpType]?.color
                }`}
              >
                {typeLabels[followUp.followUpType]?.icon}
                {typeLabels[followUp.followUpType]?.label}
              </span>
            </p>

            {/* Statut (modifiable) */}
            <p>
              <strong>Statut:</strong>{" "}
              {status === 1 ? (
                <select
                  className="select select-md select-bordered ml-2"
                  value={status}
                  onChange={(e) => {
                    const newStatus = parseInt(e.target.value);
                    setStatus(newStatus);
                    if (newStatus === 2) {
                      onRequestComplete(followUp.id);
                    } else if (newStatus === 3) {
                      onRequestCancel(followUp.id);
                    }
                  }}
                >
                  {[status, ...allowedTransitions[1]]
                    .filter((v, i, a) => a.indexOf(v) === i)
                    .map((id) => (
                      <option key={id} value={id}>
                        {statusLabels[id]?.label || `Statut ${id}`}
                      </option>
                    ))}
                </select>
              ) : (
                <span
                  className={`inline-flex items-center px-2 py-1 rounded-full ${statusLabels[status]?.color}`}
                >
                  {statusLabels[status]?.icon}
                  {statusLabels[status]?.label}
                </span>
              )}
            </p>
          </div>

          <div className="text-right mt-6">
            <button
              onClick={onClose}
              className="btn bg-primary text-white rounded-full"
            >
              Fermer
            </button>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}
